import { StandardEvents } from '@shopify/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * A dialog that shows the main content of another page of the store. Openers are plain links,
 * so a click that is not handled here still navigates to the page.
 * A collection is fetched through its `modal` template, which leaves out filters and sorting.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} content - The element the fetched content is rendered into.
 *
 * @typedef {object} PageContent
 * @property {HTMLElement} content - The main content of the fetched page.
 * @property {string[]} stylesheets - The stylesheet URLs the fetched page links in its head.
 *
 * @extends {DialogComponent}
 */
class PageDialogComponent extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {Map<string, Promise<PageContent | null>>} */
  #cachedContent = new Map();

  /** @type {string | null} */
  #activeUrl = null;

  /** @type {Map<string, Promise<unknown>>} */
  #stylesheetLoads = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * Opens the dialog with the content of the page the opener links to.
   * @param {MouseEvent} event - The click on the opener.
   */
  open = async (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const opener = /** @type {HTMLAnchorElement} */ (event.target);
    const url = this.#getContentUrl(opener);
    if (!url) return;

    event.preventDefault();
    this.#activeUrl = url;

    const page = await this.#load(url);
    if (this.#activeUrl !== url) return;

    if (!page) {
      window.location.assign(opener.href);
      return;
    }

    await this.#loadStylesheets(page.stylesheets);
    if (this.#activeUrl !== url) return;

    this.#render(page.content);
    this.showDialog();
  };

  /**
   * Fetches the content of the page the opener links to before it is clicked.
   * @param {PointerEvent} event - The pointer entering the opener.
   */
  prefetch = (event) => {
    const url = this.#getContentUrl(event.target);
    if (url) this.#load(url);
  };

  /**
   * Resolves the URL to fetch from an opener, or null when the link leaves the store.
   * @param {EventTarget | null} opener - The opener link.
   * @returns {string | null}
   */
  #getContentUrl(opener) {
    if (!(opener instanceof HTMLAnchorElement) || !opener.href) return null;

    const url = new URL(opener.href);
    if (url.origin !== window.location.origin) return null;

    if (/\/collections\/[^/]+\/?$/.test(url.pathname)) url.searchParams.set('view', 'modal');

    return url.toString();
  }

  /**
   * @param {string} url - The page to fetch.
   * @returns {Promise<PageContent | null>}
   */
  #load(url) {
    let page = this.#cachedContent.get(url);

    if (!page) {
      page = this.#fetchContent(url);
      this.#cachedContent.set(url, page);
      page.then((result) => {
        if (!result) this.#cachedContent.delete(url);
      });
    }

    return page;
  }

  /**
   * @param {string} url - The page to fetch.
   * @returns {Promise<PageContent | null>}
   */
  async #fetchContent(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.getElementById('MainContent');
      if (!content) throw new Error('No #MainContent in the fetched page');

      content.setAttribute('aria-label', html.querySelector('h1')?.textContent?.trim() || html.title);

      const stylesheets = Array.from(html.head.querySelectorAll('link[rel="stylesheet"][href]'), (link) => link.href);

      // Adopting detaches the content from the parsed page, so the rest of that page is not kept in the cache.
      return { content: document.adoptNode(content), stylesheets };
    } catch (error) {
      console.error(`[page-dialog] Failed to load ${url}:`, error);
      return null;
    }
  }

  /**
   * @param {HTMLElement} content - The fetched main content.
   */
  #render(content) {
    const { dialog, content: container } = this.refs;

    dialog.setAttribute('aria-label', content.getAttribute('aria-label') ?? '');
    container.replaceChildren(...content.cloneNode(true).childNodes);

    this.#loadModuleScripts(container);
  }

  /**
   * Shopify subsets `{% stylesheet %}` CSS to each page's render tree, so the fetched page can link a
   * stylesheet the host page does not. Resolves once the missing ones have loaded or failed, including
   * ones an earlier open added and is still waiting for.
   * @param {string[]} stylesheets - The stylesheet URLs of the fetched page.
   * @returns {Promise<unknown>}
   */
  #loadStylesheets(stylesheets) {
    const linked = new Set(Array.from(document.querySelectorAll('link[rel="stylesheet"]'), (link) => link.href));

    return Promise.all(
      stylesheets.map((href) => {
        let load = this.#stylesheetLoads.get(href);

        if (!load && !linked.has(href)) {
          load = new Promise((resolve) => {
            const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
            document.head.append(link);
          });
          this.#stylesheetLoads.set(href, load);
        }

        return load;
      })
    );
  }

  /**
   * Scripts inserted with the content never run, so a module the host page has not loaded yet
   * would leave its custom elements inert. A module runs once per URL, so loading it again is a no-op.
   * @param {HTMLElement} container - The rendered content.
   */
  #loadModuleScripts(container) {
    const selector = 'script[type="module"][src]';
    const inserted = Array.from(container.querySelectorAll(selector));
    const loaded = new Set(
      Array.from(document.querySelectorAll(selector))
        .filter((script) => !container.contains(script))
        .map((script) => script.getAttribute('src'))
    );

    for (const script of inserted) {
      const source = script.getAttribute('src');
      if (!source || loaded.has(source)) continue;

      const module = document.createElement('script');
      module.type = 'module';
      module.src = source;
      document.head.append(module);
      loaded.add(source);
    }
  }

  /**
   * Closes the dialog after an add to cart when the cart drawer or modal opens on add, so the cart is not covered.
   * An add from a dialog stacked above this one closes it at once, before that dialog's close opens the drawer.
   * A quiet add from a product card opens no cart, so the dialog stays.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event - The cart lines update event.
   */
  #handleCartLinesUpdate = (event) => {
    const { dialog } = this.refs;
    if (event.action !== 'add' || !dialog.open) return;
    if (event.target instanceof Element && event.target.closest('[data-quiet-add]')) return;
    if (!document.querySelector('cart-drawer-component[auto-open], cart-modal-component')) return;

    const instant = !(event.target instanceof Node && dialog.contains(event.target));

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError || !dialog.open) return;

        dialog.classList.toggle('page-dialog--instant', instant);
        await this.closeDialog();
        dialog.classList.remove('page-dialog--instant');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[page-dialog] Event promise rejected:', error);
      });
  };

  // A closed dialog ignores scrollTop, so the layer is reset once it shows
  #handleDialogOpen = () => {
    this.refs.dialog.scrollTop = 0;
  };

  #handleDialogClose = () => {
    this.#activeUrl = null;
    this.refs.content.replaceChildren();
  };
}

if (!customElements.get('page-dialog-component')) {
  customElements.define('page-dialog-component', PageDialogComponent);
}

const template = document.querySelector('template[data-page-dialog]');
if (template instanceof HTMLTemplateElement && !document.getElementById('page-dialog')) {
  document.body.append(template.content.cloneNode(true));
}
